import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbTypeahead, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { InstrumentSearchFacadeService, InstrumentSearchResult } from '@zero/search/domain';
import { HasAssetPipe, HasMultiOrderInstrumentPipe } from '@zero/shared/ui-pipes';
import { Asset } from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import { MultiOrderInstrument } from '@zero/trade/domain';
import { commonRoutes, famousIndications } from 'constants/web-app-constants';
import { Observable, Subject, debounceTime, distinctUntilChanged, filter, firstValueFrom, merge, switchMap } from 'rxjs';

@Component({
  selector: 'web-components-asset-search',
  templateUrl: './asset-search.component.html',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    FormsModule,
    NgbTypeaheadModule,
    NgClass,
    TranslocoPipe,
    HasMultiOrderInstrumentPipe,
    HasAssetPipe,
  ],
})
export class AssetSearchComponent {
  @Input() public clearAfterSelect = false;
  @Input() public isSmall = true;
  @Input() public type: AssetSearchType = 'default';

  @Input() public isDisabled = false;
  @Input() public chosenAssets: Asset[] | MultiOrderInstrument[];

  @Output() public assetSelected = new EventEmitter<any>();
  @Output() public silentAssetSelected = new EventEmitter<void>();
  @ViewChild('searchInput') public searchInput: ElementRef;

  public simulateInputValueChange = new Subject<string>();
  public model: any;
  public hasFocus = false;
  public commonRoutes = commonRoutes;

  readonly #typingDebounceTime = 350;

  readonly #router = inject(Router);
  readonly #instrumentSearchService = inject(InstrumentSearchFacadeService);
  readonly #assetSearchService = inject(AssetSearchService);
  readonly #destroyRef = inject(DestroyRef);

  constructor() {
    this.#setupSearchEvents();
  }

  public showDetails(isin: string): void {
    this.#router.navigate([commonRoutes.postlogin.instrument_detail, isin]).finally();
    this.simulateInputValueChange.next('');
  }

  public savingsPlansSearch = (text$: Observable<string>): Observable<any[]> =>
    this.#textObservable(text$).pipe(
      filter((value) => value.trim() !== ''),
      switchMap((searchText) => this.#handleSavingsPlanSearch(searchText)),
    );

  public underlyingSearch = (text$: Observable<string>): Observable<any[]> =>
    this.#textObservable(text$).pipe(
      filter((value) => value.trim() !== ''),
      switchMap((searchText) => this.#handleUnderlyingSearch(searchText)),
    );

  public search = (text$: Observable<string>): Observable<any[]> =>
    this.#textObservable(text$).pipe(
      filter((value) => value.trim() !== ''),
      switchMap((searchText) => this.#handleSearch(searchText)),
    );

  public formatter = (x: { name: string }): string => x.name;

  public itemSelected(asset: any): void {
    if (asset.item.headline || asset.item.name === '-') {
      asset.preventDefault();
      return;
    }

    const inputFieldVal = typeof this.searchInput?.nativeElement?.value === 'string' ? this.searchInput.nativeElement.value : '';

    switch (this.type) {
      case 'header':
        // Nach dem Emit wird das Eingabefeld durch die NgbTypeahead-Komponente auf den selektierten Werte geändert.
        // Das wollen wir im Header nicht. Mit einem setTimeout ohne Angabe von Millisekunden sorgen wir dafür,
        // dass der eingetippte Wert schnellstmöglich wieder zurückgesetzt wird.
        setTimeout(() => {
          if (this.model && typeof this.model === 'string') {
            this.searchInput.nativeElement.value = this.model;
          } else {
            this.searchInput.nativeElement.value = inputFieldVal;
          }
          this.silentAssetSelected.emit();
        });
        break;
      case 'watchlist':
        this.assetSelected.emit(asset.item);
        setTimeout(() => {
          this.searchInput.nativeElement.value = '';
          if (this.searchInput.nativeElement.blur) {
            this.searchInput.nativeElement.blur();
          }
        });
        break;
      case 'underlying':
        {
          const { item } = asset;
          if (!item) {
            throw new Error('underlying search failed');
          }
          const { name, isin, wkn } = item;
          this.assetSelected.emit({
            name,
            isin,
            wkn,
          });
        }
        break;
      default:
        this.#assetSearchService
          .loadAssetByIsin(asset.item.isin)
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe((loadedAsset) => this.assetSelected.emit(loadedAsset));
        break;
    }
  }

  public clearInput(el: any): void {
    this.model = '';

    this.assetSelected.emit(undefined);

    setTimeout(() => {
      el.value = '';
      this.simulateInputValueChange.next('');
      el.focus();
    });
  }

  public preventSelection($event: Event): boolean {
    $event.stopPropagation();
    return false;
  }

  public repeatLastInput(): void {
    if (this.model && typeof this.model === 'string' && this.model !== '') {
      this.simulateInputValueChange.next(this.model);
    } else if (typeof this.searchInput?.nativeElement?.value === 'string' && this.searchInput.nativeElement.value !== '') {
      this.simulateInputValueChange.next(this.searchInput.nativeElement.value);
    }
  }

  public gotFocus(): void {
    this.hasFocus = true;
  }

  public handleBlur = (): void => {
    this.hasFocus = false;
  };

  readonly #csvAssetMapper = (csvData: string): any => {
    const sProdValues = csvData.split(';');

    return {
      isin: sProdValues[0],
      wkn: sProdValues[1] ? sProdValues[1] : sProdValues[0].slice(5, 11),
      name: sProdValues[2],
      aliases: sProdValues[3],
      cat: sProdValues[4] ? sProdValues[4] : 'S',
      isPossibleSavingsPlan: sProdValues[5] === 'S',
    };
  };

  readonly #setupSearchEvents = (): void => {
    const originalNgbTypeaheadhandleKeyDownFn = NgbTypeahead.prototype.handleKeyDown;
    const showDetails = this.showDetails.bind(this);
    NgbTypeahead.prototype.handleKeyDown = function (event) {
      if (event.type === 'keydown' && event.code === 'Enter') {
        try {
          const ev: KeyboardEvent = event;
          const activeElement: Element = (ev.target as HTMLElement).parentElement.getElementsByClassName('active')[0];
          const isin = activeElement.children[0]?.getAttribute('data-isin');
          isin && showDetails(isin);
        } catch (e) {
          return originalNgbTypeaheadhandleKeyDownFn.call(this, event);
        }
      }
      return originalNgbTypeaheadhandleKeyDownFn.call(this, event);
    };
    if (this.type !== 'header' && this.searchInput) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 200);
    }
  };

  readonly #handleSearch = async (text: string) => {
    const result = [];
    const { results } = await firstValueFrom(this.#instrumentSearchService.searchByQuery(text, { size: 3 }));

    const cryptos = results.filter(({ type }) => type === 'KRYPTO').map(this.#instrumentSearchResultMapper);
    if (cryptos.length) {
      result.push({ headline: 'Kryptos' });
      Array.prototype.push.apply(result, cryptos);
    }

    const stocks = results.filter(({ type }) => type === 'AKTIE').map(this.#instrumentSearchResultMapper);
    result.push({ headline: 'Aktien' });
    if (stocks.length) {
      Array.prototype.push.apply(result, stocks);
    } else {
      result.push(this.#csvAssetMapper('-;-;-;-'));
    }

    const funds = results
      .filter(({ type }) => type === 'ETF_ETC' || type === 'FONDS' || type === 'ETF' || type === 'ETC')
      .map(this.#instrumentSearchResultMapper);
    result.push({ headline: 'ETFs, Fonds' });
    if (funds.length) {
      Array.prototype.push.apply(result, funds);
    } else {
      result.push(this.#csvAssetMapper('-;-;-;-'));
    }

    const derivatives = results.filter(({ type }) => type === 'DERIVAT').map(this.#instrumentSearchResultMapper);
    result.push({ headline: 'Zertifikate, Derivate...' });
    if (derivatives.length) {
      Array.prototype.push.apply(result, derivatives);
    } else {
      result.push(this.#csvAssetMapper('-;-;-;-'));
    }

    return result;
  };

  readonly #instrumentSearchResultMapper = (item: InstrumentSearchResult): any => {
    return {
      isin: item.isin,
      wkn: item.wkn,
      name: item.longName,
      aliases: item.longName,
      cat: item.type,
      shortName: item.shortName,
    };
  };

  readonly #handleSavingsPlanSearch = async (text: string) => {
    const result = [];
    const { results } = await firstValueFrom(
      this.#instrumentSearchService.searchByQuery(text, {
        sparplan: true,
      }),
    );

    const products = results.map(this.#instrumentSearchResultMapper);
    result.push({ headline: 'Sparplanfähige Instrumente' });
    if (products.length) {
      Array.prototype.push.apply(result, products);
    } else {
      result.push(this.#csvAssetMapper('-;-;-;-'));
    }

    return result;
  };

  readonly #handleUnderlyingSearch = async (text: string) => {
    const result = [];
    const indices = this.#getIndicesByTerm(text);
    result.push({ headline: 'Indizes' });
    if (indices.length > 0) {
      Array.prototype.push.apply(result, indices);
    } else {
      result.push(this.#csvAssetMapper('-;-;-;-'));
    }

    const { results } = await firstValueFrom(
      this.#instrumentSearchService.searchByQuery(text, {
        type: ['AKTIE'],
      }),
    );

    const stocks = results.map(this.#instrumentSearchResultMapper);
    result.push({ headline: 'Aktien' });
    if (stocks.length) {
      Array.prototype.push.apply(result, stocks);
    } else {
      result.push(this.#csvAssetMapper('-;-;-;-'));
    }

    return result;
  };

  readonly #getIndicesByTerm = (term: string) => {
    const termUpper = term
      .toUpperCase()
      .split(' ')
      .filter((token) => token !== '');
    return famousIndications
      .filter((index) => {
        const { name, isin } = index;
        for (const char of termUpper) {
          if (name.toUpperCase().indexOf(char) > -1 || isin.toUpperCase().indexOf(char) > -1) {
            return index;
          }
        }

        return null;
      })
      .slice(0, 5);
  };

  readonly #textObservable = (text$: Observable<string>): Observable<string> =>
    merge(text$.pipe(debounceTime(this.#typingDebounceTime), distinctUntilChanged()), this.simulateInputValueChange);
}

type AssetSearchType =
  | 'header'
  | 'default'
  | 'savings-plan'
  | 'savings-plan-create-form'
  | 'savings-plan-modal'
  | 'watchlist'
  | 'underlying'
  | 'multi-order'
  | 'savings-plan-onboarding';
